import type { ReferencedValue, ValueTombstone } from "@skilitics/nautilus-core-designer-data";

import type { ResolveValueToConditionValue } from "../resolveValueConfigs.ts";
import type { ResolveValueSignature } from "../resolveValueSignature.ts";
import type { GetValueSubtypeDefinition } from "../subtype-definitions/index.ts";
import type { BuildRuntimeExpressionContext } from "./buildRuntimeExpression.ts";

interface CreateOptions {
  resolveValueSignature: ResolveValueSignature;
  getValueSubtypeDefinition: GetValueSubtypeDefinition;
  resolveValue: ResolveValueToConditionValue;
}

/**
 * Everything the assembly needs, from the subject alone. `undefined` where the
 * subject says nothing a condition could be built from.
 */
export type ResolveValueExpressionContext = (
  subject: ReferencedValue | ValueTombstone,
) => BuildRuntimeExpressionContext | undefined;

/**
 * The route from a picked subject to a build context, for a consumer whose
 * expression is over a value it cannot name — the utility condition, and anything
 * else that lets an author choose what to compare.
 *
 * A node-owned builder has no use for this: it names a property of its own result
 * and knows the type it compares, so it assembles a context directly. What is
 * gathered here is the part only the subject can answer.
 */
export const resolveValueExpressionContextCreate = ({
  resolveValueSignature,
  getValueSubtypeDefinition,
  resolveValue,
}: CreateOptions): ResolveValueExpressionContext => {
  return (subject) => {
    const signature = resolveValueSignature(subject);
    if (!signature) return undefined;

    const definition = getValueSubtypeDefinition(signature);
    // the subject is the type authority, so nothing narrows it; a tombstoned or
    // dangling subject resolves to undefined and leaves nothing to compare
    const resolved = resolveValue(subject, definition.type);
    if (resolved === undefined) return undefined;

    return { signature, definition, resolveValue, subject: resolved };
  };
};
